import { type PolylineProps } from '@react-google-maps/api'

import { MapsRepository } from '../infra/maps/MapsRepository'
import { type Coordinate } from '../ui/components/map/Map'

export type GetMapRoutesRepositoryParams = {
  repository: MapsRepository
}

export type GetMapRoutesParams = {
  alternativeRouteColor: string
  alternativeRouteWeight: number
  primaryRouteColor: string
  primaryRouteWeight: number
  coordinates: Coordinate[]
  showAlternativeRoutes: boolean
}

export function getMapsRoutesFactory({ repository }: GetMapRoutesRepositoryParams) {
  return async function getMapRoutesImpl(params: GetMapRoutesParams) {
    const {
      alternativeRouteColor,
      alternativeRouteWeight,
      coordinates,
      primaryRouteColor,
      primaryRouteWeight,
      showAlternativeRoutes,
    } = params

    const response = await repository.route(coordinates)

    if (!response) return []

    let routes: PolylineProps[] = []

    for (let i = response.routes.length - 1; i > -1; i--) {
      const legs = response.routes[i].legs

      const isAlternativeRoute = i > 0
      if (!showAlternativeRoutes && isAlternativeRoute) {
        continue
      }

      const strokeColor = isAlternativeRoute ? alternativeRouteColor : primaryRouteColor
      const strokeWeight = isAlternativeRoute ? alternativeRouteWeight : primaryRouteWeight

      for (const legElement of legs) {
        let steps = legElement.steps

        for (const stepElement of steps) {
          let nextSegment = stepElement.path

          let polyline: any = {
            path: [],
            options: {
              strokeColor,
              strokeWeight,
            },
          }

          polyline.path.push(nextSegment[0], nextSegment[Math.floor(nextSegment.length / 8)])
          polyline.path.push(
            nextSegment[Math.floor(nextSegment.length / 8)],
            nextSegment[Math.floor(nextSegment.length / 8) * 2]
          )
          polyline.path.push(
            nextSegment[Math.floor(nextSegment.length / 8) * 2],
            nextSegment[Math.floor(nextSegment.length / 8) * 3]
          )
          polyline.path.push(
            nextSegment[Math.floor(nextSegment.length / 8) * 3],
            nextSegment[Math.floor(nextSegment.length / 8) * 4]
          )
          polyline.path.push(
            nextSegment[Math.floor(nextSegment.length / 8) * 4],
            nextSegment[Math.floor(nextSegment.length / 8) * 5]
          )
          polyline.path.push(
            nextSegment[Math.floor(nextSegment.length / 8) * 5],
            nextSegment[Math.floor(nextSegment.length / 8) * 6]
          )
          polyline.path.push(
            nextSegment[Math.floor(nextSegment.length / 8) * 6],
            nextSegment[Math.floor(nextSegment.length / 8) * 7]
          )
          polyline.path.push(
            nextSegment[Math.floor(nextSegment.length / 8) * 7],
            nextSegment[nextSegment.length - 1]
          )

          routes.push(polyline)
        }
      }
    }

    return routes
  }
}

export function getMapRoutes(params: GetMapRoutesParams) {
  const repository = new MapsRepository({
    directionsService: new google.maps.DirectionsService(),
    geocodeService: new google.maps.Geocoder(),
  })
  const getRoutes = getMapsRoutesFactory({ repository: repository })

  return getRoutes(params)
}
